import { FC, useState } from 'react';
import {
	IconButton,
	InputAdornment,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import './InlineField.scss';
import {
	ERRORS,
	vHasSpecialCharacters,
	vHasSpecialCharactersForName,
	vMaxLength,
	vMinLength,
} from '../../Form/form.validations';

type InlineFieldProps = {
	contentValue: string;
	emptyValue: string;
	disabled: boolean | undefined;
	validation: {
		required?: boolean;
		maxLength?: number;
		minLength?: number;
		forbidSpecialCharacters?: boolean;
		forbidSpecialCharactersForName?: boolean;
	};
	onSave: (value: string) => void;
};

const InlineField: FC<InlineFieldProps> = ({
	contentValue,
	emptyValue,
	validation,
	disabled,
	onSave,
}) => {
	const { t } = useTranslation();
	const [showEditableField, setShowEditableField] = useState<boolean>(false);
	const [value, setValue] = useState<string>(contentValue);
	const [masterValue] = useState<string>(contentValue);
	const [validationError, setValidationError] = useState<string>('');
	const theme = useTheme();

	const validate = () => {
		const {
			required,
			minLength,
			maxLength,
			forbidSpecialCharacters,
			forbidSpecialCharactersForName,
		} = validation;
		if (required && !value) {
			setValidationError(t(ERRORS.REQUIRED));
			return false;
		}
		if (maxLength && !vMaxLength(value, maxLength)) {
			setValidationError(
				t(ERRORS.MAX_LENGTH, {
					number: maxLength,
				}),
			);
			return false;
		}
		if (minLength && !vMinLength(value, minLength) && value) {
			setValidationError(
				t(ERRORS.MIN_LENGTH, {
					number: minLength,
				}),
			);
			return false;
		}
		if (forbidSpecialCharacters && vHasSpecialCharacters(value)) {
			setValidationError(t(ERRORS.HAS_SPECIAL_CHAR));
			return false;
		}
		if (forbidSpecialCharactersForName && vHasSpecialCharactersForName(value)) {
			setValidationError(t(ERRORS.HAS_SPECIAL_CHAR));
			return false;
		}
		return true;
	};

	const onBlur = () => {
		if (validate()) {
			setShowEditableField(false);
			setValidationError('');
			onSave(value);
		}
	};

	return (
		<div className="InlineField">
			<div className="editable-field">
				<Typography component="div" variant="body1">
					{!showEditableField && (
						<div
							className="editable-field__inner-content"
							onClick={() => (!disabled ? setShowEditableField(true) : null)}
						>
							{contentValue || emptyValue}
							{!disabled && (
								<EditIcon
									className="editable-field__edit-icon"
									sx={{ fontSize: '15px' }}
								/>
							)}
						</div>
					)}
					{showEditableField && (
						<div>
							<TextField
								type="text"
								autoFocus
								variant="standard"
								onBlur={onBlur}
								defaultValue={value}
								onChange={(e) => {
									setValue(e.target.value);
								}}
								InputProps={{
									sx: {
										color: theme.palette.primary.main,
									},
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label="toggle password visibility"
												onClick={() => {
													setValue(masterValue);
													setValidationError('');
													setShowEditableField(false);
												}}
												style={{
													color: theme.palette.primary.main,
												}}
											>
												<Close />
											</IconButton>
										</InputAdornment>
									),
								}}
							/>
						</div>
					)}
				</Typography>
				<div className="editable-field__error">{validationError}</div>
			</div>
		</div>
	);
};

export default InlineField;
